var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useRef, useState, useEffect } from 'react';
import { convertColorToThemeHexCode } from 'Styles/colors.util';
import hive from '@bombas/hive';
import InView from '../../../../../Carousel/InView';
import { useTrackInteractedSlide } from '../../ResponsiveImageGallery.hooks';
import ProductImageZoom from '../../../ProductImageZoom/ProductImageZoom';
import * as Styled from './ImageCarouselMedium.styled';
import SelectThumbnail from '../SelectThumbnail/SelectThumbnail';
/**
 * Component that renders Responsive Images in a Carousel with thumbnails below it
 */
var ImageCarouselMedium = function (_a) {
    var images = _a.images;
    var carouselRef = useRef(null);
    var _b = useState(0), currentSlideInView = _b[0], setCurrentSlideInView = _b[1];
    var _c = useState(false), hasSnappingRule = _c[0], setHasSnappingRule = _c[1];
    var _d = useState(false), isCarouselRefLoaded = _d[0], setIsCarouselRefLoaded = _d[1];
    var _e = useState(false), isZoomModalOpen = _e[0], setZoomModalOpen = _e[1];
    var _f = useState(images), zoomedImages = _f[0], setZoomedImages = _f[1];
    var _g = useState(false), isPointer = _g[0], setIsPointer = _g[1];
    var _h = useState(''), direction = _h[0], setDirection = _h[1];
    var _j = useState(0), clickedSlide = _j[0], setClickedSlide = _j[1];
    var _k = useState(false), isUserScrolling = _k[0], setUserScrolling = _k[1];
    var _l = useState(false), isUserTabbing = _l[0], setUserTabbing = _l[1];
    var handleImageZoom = function (e) {
        // checking for window adds support for SSR, where window is undefined on the server and causes errors in rendering
        if (typeof window !== 'undefined') {
            hive.track('ZOOM_LAUNCH');
        }
        var clickedImageId = e.currentTarget.dataset.id;
        var selectedImage = images.find(function (image) { return image.id.toString() === clickedImageId; });
        // put the selected image at the top
        var restImages = __spreadArray([], images, true);
        restImages.splice(restImages.indexOf(selectedImage), 1);
        setZoomedImages(__spreadArray([selectedImage], restImages, true));
        setZoomModalOpen(!isZoomModalOpen);
    };
    var handleTouchMove = function (e) {
        setClickedSlide(null);
        setUserScrolling(true);
        setUserTabbing(false);
        // note: we aware this would also launch with other 2 finger touches like a tap and we are ok with it
        var isPinched = e.targetTouches.length === 2;
        if (isPinched) {
            handleImageZoom(e);
        }
    };
    var imageInteractionTracker = useTrackInteractedSlide({
        current: currentSlideInView,
        images: images
    });
    var handleInViewChange = function (slide, isInView) {
        if (isInView) {
            setCurrentSlideInView(slide);
        }
    };
    var handleInfiniteLoop = function () {
        if (!carouselRef.current) {
            return;
        }
        setUserScrolling(false);
        setClickedSlide(direction === 'next' ? 0 : images.length - 1);
        setHasSnappingRule(false);
        var scrollWidth = carouselRef.current.scrollWidth;
        carouselRef.current.scrollTo({
            left: direction === 'next' ? 0 : scrollWidth,
            behavior: 'smooth'
        });
    };
    var onThumbnailClick = function (slideNumber) {
        if (!carouselRef.current) {
            return;
        }
        setClickedSlide(slideNumber);
        setUserScrolling(false);
        setUserTabbing(false);
        var slides = carouselRef.current.children;
        var offsetWidth = slides[0].offsetWidth;
        carouselRef.current.scrollTo({
            left: slideNumber * offsetWidth,
            behavior: 'smooth'
        });
    };
    var onSlideChange = function () {
        if (!carouselRef.current) {
            return;
        }
        setClickedSlide(null);
        setUserScrolling(true);
        setUserTabbing(false);
        setIsPointer(false);
        var slides = carouselRef.current.children;
        var offsetWidth = slides[0].offsetWidth;
        var currentScrollPosition = carouselRef.current.scrollLeft;
        var sign = direction === 'next' ? 1 : -1;
        carouselRef.current.scrollTo({
            left: currentScrollPosition + sign * offsetWidth,
            behavior: 'smooth'
        });
    };
    var carouselImages = images.map(function (image, index) { return (React.createElement(Styled.Image, { key: image.id || index, "data-id": image.id || index, background: convertColorToThemeHexCode('grey20'), resolution: { medium: 840 }, crop: {
            height: 3040,
            width: 3040
        }, src: image.src, alt: image.alt, role: index !== 0 ? 'presentation' : undefined, onClick: handleImageZoom, onTouchMove: handleTouchMove, isLazy: index !== 0, withProgressiveLoading: index !== 0 })); });
    useEffect(function () {
        var _a;
        if (isCarouselRefLoaded) {
            (_a = carouselRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo({ left: 0 });
        }
        setClickedSlide(0);
        setCurrentSlideInView(0);
        imageInteractionTracker.reset();
    }, [images.length, images[0].id]);
    useEffect(function () { return setIsCarouselRefLoaded(!!carouselRef.current); }, [carouselRef.current]);
    return (React.createElement(Styled.ImageCarouselMediumContainer, null,
        React.createElement(Styled.ImageCarouselMedium, { ref: carouselRef, onMouseEnter: function () {
                setHasSnappingRule(true);
                if (carouselRef.current) {
                    carouselRef.current.focus({
                        preventScroll: true
                    });
                }
            }, onTouchStart: function () { return setHasSnappingRule(true); }, onWheel: function () {
                setClickedSlide(null);
                setUserScrolling(true);
                setUserTabbing(false);
            }, onKeyDown: function (e) {
                setHasSnappingRule(true);
                if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    setDirection('previous');
                    if (currentSlideInView === 0) {
                        handleInfiniteLoop();
                    }
                    else {
                        onSlideChange();
                    }
                }
                if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    setDirection('next');
                    if (currentSlideInView === images.length - 1) {
                        handleInfiniteLoop();
                    }
                    else {
                        onSlideChange();
                    }
                }
                if (e.key === 'Tab') {
                    setUserTabbing(true);
                }
            }, hasSnappingRule: hasSnappingRule, tabIndex: 0 }, carouselImages.map(function (image, index) { return (React.createElement(Styled.Content, { tabIndex: 0, key: index, isPointer: isPointer },
            React.createElement(InView, { parent: carouselRef.current, setElementInView: handleInViewChange, position: index, withRootMargin: false, threshold: [0, 0.8], isLazy: false }, image))); })),
        React.createElement(SelectThumbnail, { images: images, onThumbnailClick: onThumbnailClick, slideInView: currentSlideInView, clickedSlide: clickedSlide, isUserScrolling: isUserScrolling, isUserTabbing: isUserTabbing }),
        React.createElement(ProductImageZoom, { "data-testid": "image_zoom_modal", isOpen: isZoomModalOpen, images: zoomedImages, onRequestClose: function () { return setZoomModalOpen(false); } })));
};
export default ImageCarouselMedium;
